// =====================================================================
// Admin Dashboard — Mik-San B2B yonetici merkezi anasayfa
// =====================================================================
// DEGISIKLIK GECMISI:
//  - Faz 7  (orijinal): 4 KPI kart, 14 gunluk satis grafigi, top 10 urun,
//                       low stock uyari listesi, recent orders, kampanya kartlari
//  - Faz S1.3 (2026-04-29): 5. KPI eklendi -> "Bekleyen Onaylar".
//                            Tiklanir KpiCard (onClick prop) + pulse animasyonu (alert prop).
//                            md=2.4 grid (12/5) ile 1 satira sigar.
//  - Faz 12a  (2026-04-29): KPI altina "Sistem Sagligi" mini widget satiri eklendi.
//                            getSystemHealth() ile API uptime, DB latency, hata 24sa,
//                            audit 1sa, email 24sa, failed login 1sa metrikleri chip
//                            olarak gosterilir. Tiklanir -> /audit-log sayfasina gider.
//                            Status (ok/warning/error) renk kodu + pulse + warning Alert.
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Grid, Card, Typography, Box, CircularProgress, Chip, Table,
  TableHead, TableRow, TableCell, TableBody, Paper, IconButton,
  Tooltip as MuiTooltip, Alert, Button, Avatar, LinearProgress
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PeopleIcon from "@mui/icons-material/People";
import WarningIcon from "@mui/icons-material/Warning";
import CampaignIcon from "@mui/icons-material/Campaign";
import StarIcon from "@mui/icons-material/Star";
// Faz S1.3 (2026-04-29): Bekleyen Onaylar KPI karti icin uyari ikonu
// Faz 12a (2026-04-29): Sistem Sagligi widget'i icin metrik chip'leri
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
// Faz BG (2026-05-10): Bugün Yapılacaklar widget
import TodayActionsWidget from "../components/TodayActionsWidget";
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend
} from "recharts";
import {
  getTodaySummary, getAnalyticsDaily, getTopProducts,
  getLowStock, getActiveCampaigns, getOrdersAll, getUsers,
  getSystemHealth
} from "../services/api";

const STATUS_LABELS = {
  pending: "Bekliyor", confirmed: "Onaylandı", shipped: "Kargoda",
  delivered: "Teslim", cancelled: "İptal"
};
const STATUS_COLORS = {
  pending: "#F59E0B", confirmed: "#10B981", shipped: "#8B5CF6",
  delivered: "#1565C0", cancelled: "#EF4444"
};

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
const fmtMoneyShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M ₺";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "B ₺";
  return n.toLocaleString("tr-TR", { maximumFractionDigits: 0 }) + " ₺";
};
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR") : "—";

// =====================================================================
// KpiCard — Dashboard KPI kart bileseni
// Faz S1.3 (2026-04-29): onClick + alert prop'lari eklendi.
//   - onClick: tiklanabilir kart (orn. Bekleyen Onaylar -> /pending'e gider)
//   - alert: true ise dikkat ceken pulse animasyonu (sayinin > 0 oldugu durumlar)
// Eski 4 statik kart pattern korundu, sadece bu iki prop opsiyonel olarak eklendi.
// =====================================================================
const KpiCard = ({ title, value, sub, icon, color, onClick, alert }) => (
  <Card
    onClick={onClick}
    sx={{
      p: 2.5, borderRadius: 3, height: "100%", display: "flex", alignItems: "center", gap: 2,
      cursor: onClick ? "pointer" : "default",
      transition: "transform .15s, box-shadow .15s",
      ...(onClick ? { "&:hover": { transform: "translateY(-2px)", boxShadow: 4 } } : {}),
      ...(alert ? {
        boxShadow: "0 0 0 2px " + color + "55",
        animation: "kpiPulse 2s ease-in-out infinite",
        "@keyframes kpiPulse": {
          "0%, 100%": { boxShadow: "0 0 0 2px " + color + "55" },
          "50%": { boxShadow: "0 0 0 6px " + color + "22" },
        }
      } : {})
    }}
  >
    <Box sx={{ bgcolor: color, color: "white", borderRadius: 2, p: 1.5, display: "flex" }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h5" fontWeight={800} lineHeight={1.1} noWrap>{value}</Typography>
      <Typography variant="body2" color="text.secondary" mt={0.5}>{title}</Typography>
      {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
    </Box>
  </Card>
);

const daysLeft = (endAt) => {
  if (!endAt) return null;
  const ms = new Date(endAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [daily, setDaily] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [recent, setRecent] = useState([]);
  const [pendingCount, setPendingCount] = useState(0); // Bekleyen onay sayisi
  const [health, setHealth] = useState(null); // Faz 12a: sistem sagligi
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      getTodaySummary().catch(() => ({ data: {} })),
      getAnalyticsDaily(14).catch(() => ({ data: [] })),
      getTopProducts(30, 10).catch(() => ({ data: [] })),
      getLowStock().catch(() => ({ data: [] })),
      getActiveCampaigns().catch(() => ({ data: [] })),
      getOrdersAll().catch(() => ({ data: [] })),
      // Bekleyen onay sayisi — getUsers tum kullanicilari doner, status=pending_approval filtrele
      getUsers().catch(() => ({ data: [] })),
      // Faz 12a: sistem sagligi metrikleri
      getSystemHealth().catch(() => ({ data: null })),
    ]).then(([t, d, tp, ls, cp, ord, us, sh]) => {
      setToday(t.data || {});
      setDaily(Array.isArray(d.data) ? d.data : []);
      setTopProducts(Array.isArray(tp.data) ? tp.data : []);
      const lsList = Array.isArray(ls.data) ? ls.data : (ls.data?.products || []);
      setLowStock(lsList);
      const cpList = (Array.isArray(cp.data) ? cp.data : []).filter(c => {
        if (!c.is_active) return false;
        if (c.end_at && new Date(c.end_at) < new Date()) return false;
        return true;
      });
      setCampaigns(cpList);
      const ordList = Array.isArray(ord.data) ? ord.data : [];
      setRecent(ordList.slice(0, 10));
      // Pending onay sayisi (Layout.js ile ayni mantik)
      const usList = Array.isArray(us.data) ? us.data : [];
      setPendingCount(usList.filter(u => u.status === "pending_approval").length);
      // Faz 12a: sistem sagligi
      setHealth(sh.data || null);
      setErr(false);
      setLastRefresh(new Date());
    }).catch(() => setErr(true)).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (loading && !today) {
    return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;
  }
  if (err && !today) {
    return <Alert severity="error" sx={{ mt: 4 }}>Dashboard verileri yüklenemedi. API'yi kontrol edin.</Alert>;
  }

  const t = today || {};
  const chartData = daily.map(d => ({
    date: new Date(d.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" }),
    Sipariş: Number(d.orders) || 0,
    Ciro: Number(d.revenue) || 0
  }));

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={800}>Dashboard</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {lastRefresh && (
            <Typography variant="caption" color="text.disabled">
              Son güncelleme: {lastRefresh.toLocaleTimeString("tr-TR")}
            </Typography>
          )}
          <MuiTooltip title="Yenile">
            <IconButton size="small" onClick={loadAll}><RefreshIcon fontSize="small" /></IconButton>
          </MuiTooltip>
        </Box>
      </Box>

      {/* Faz BG: Bugün Yapılacaklar — owner sabah ilk gördüğü kart */}
      <TodayActionsWidget />

      {/* KPI Kartlari — 5 kart, md=2.4 (12/5) ile 1 satira sigar
          Faz AL hotfix: mb={3} bazı MUI versiyonlarda Grid container'da yetersiz —
          sx ile margin-bottom 24px açıkça veriyoruz, alttaki "Sistem Normal" Card'ı
          ile çakışmasın. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Bugünkü Sipariş"
            value={(t.today_orders || 0).toLocaleString("tr-TR")}
            sub={t.today_avg ? `Ort. sepet: ${fmtMoneyShort(t.today_avg)}` : null}
            icon={<ShoppingCartIcon />}
            color="#1565C0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Bugünkü Ciro"
            value={fmtMoneyShort(t.today_revenue)}
            sub={fmtMoney(t.today_revenue)}
            icon={<TrendingUpIcon />}
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Aktif Kullanıcı"
            value={(t.active_users || 0).toLocaleString("tr-TR")}
            sub="Onaylanmış üyeler"
            icon={<PeopleIcon />}
            color="#8B5CF6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Düşük Stok"
            value={(t.low_stock_count || 0).toLocaleString("tr-TR")}
            sub="Stok ≤ minimum"
            icon={<WarningIcon />}
            color="#EF4444"
          />
        </Grid>
        {/* Faz S1.3 (2026-04-29): Bekleyen Onaylar — tiklanir kart, /pending'e gider.
            Sayi > 0 ise pulse animasyonu ile dikkat ceker. */}
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title="Bekleyen Onaylar"
            value={pendingCount.toLocaleString("tr-TR")}
            sub={pendingCount > 0 ? "Yeni kayıt onayı bekliyor →" : "Tümü onaylandı ✓"}
            icon={<NotificationsActiveIcon />}
            color="#F59E0B"
            onClick={() => navigate("/pending")}
            alert={pendingCount > 0}
          />
        </Grid>
      </Grid>

      {/* Faz 12a (2026-04-29): Sistem Sagligi mini widget satiri.
          5 kucuk metrik chip — API durumu, DB latency, hata sayisi, audit/sa, email/sa.
          Tiklanabilir: tum satir /audit-log'a gider. */}
      {health && (
        <Card
          sx={{
            p: 1.5, px: 2, borderRadius: 3, mb: 3,
            display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2,
            cursor: "pointer", transition: "background .15s",
            bgcolor: health.status === "ok" ? "#F0FDF4" : (health.status === "warning" ? "#FFFBEB" : "#FEF2F2"),
            border: "1px solid",
            borderColor: health.status === "ok" ? "#86EFAC" : (health.status === "warning" ? "#FDE68A" : "#FCA5A5"),
            "&:hover": { filter: "brightness(.97)" }
          }}
          onClick={() => navigate("/audit-log")}
        >
          <Box display="flex" alignItems="center" gap={1} mr={1}>
            <Box
              sx={{
                width: 10, height: 10, borderRadius: "50%",
                bgcolor: health.status === "ok" ? "#16A34A" : (health.status === "warning" ? "#F59E0B" : "#DC2626"),
                animation: "healthPulse 2s ease-in-out infinite",
                "@keyframes healthPulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.4 }
                }
              }}
            />
            <Typography variant="body2" fontWeight={700}>
              Sistem: {health.status === "ok" ? "Normal" : (health.status === "warning" ? "Uyarı" : "Hata")}
            </Typography>
          </Box>
          <Chip size="small" variant="outlined" label={`API ${health.uptime_seconds < 3600 ? Math.floor(health.uptime_seconds / 60) + 'dk' : Math.floor(health.uptime_seconds / 3600) + 'sa'}`} />
          <Chip size="small" variant="outlined" label={`DB ${health.db_latency_ms}ms`} color={health.db_latency_ms > 200 ? "warning" : "default"} />
          <Chip size="small" variant="outlined" label={`Hata 24sa: ${health.metrics?.errors_24h || 0}`} color={(health.metrics?.errors_24h || 0) > 5 ? "warning" : "default"} />
          <Chip size="small" variant="outlined" label={`Audit 1sa: ${health.metrics?.audit_log_1h || 0}`} />
          <Chip size="small" variant="outlined" label={`Email 24sa: ${health.metrics?.email_sent_24h || 0}`} />
          <Chip size="small" variant="outlined" label={`Failed login 1sa: ${health.metrics?.failed_logins_1h || 0}`} color={(health.metrics?.failed_logins_1h || 0) > 10 ? "error" : "default"} />
          {/* Faz 14b: Sepeti terk hatirlatma metrikleri */}
          <Chip size="small" variant="outlined" label={`Sepet hatırl. 24sa: ${health.metrics?.abandoned_cart_reminders_24h || 0}`} />
          <Chip size="small" variant="outlined" label={`Geri kazanım 7g: ${health.metrics?.abandoned_cart_recovered_7d || 0}`} color={(health.metrics?.abandoned_cart_recovered_7d || 0) > 0 ? "success" : "default"} />
          {/* Faz 14a: Welcome email otomasyonu metric */}
          <Chip size="small" variant="outlined" label={`Welcome 24sa: ${health.metrics?.welcome_email_sent_24h || 0}`} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            v{health.version} · audit log → tıkla
          </Typography>
        </Card>
      )}
      {health?.warnings && health.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {health.warnings.join(" · ")}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Sol: 14 günlük satış grafiği */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Son 14 Gün — Satış Trendi
            </Typography>
            {chartData.length === 0 ? (
              <Alert severity="info">Henüz satış verisi yok.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}
                         tickFormatter={(v) => fmtMoneyShort(v)} />
                  <Tooltip
                    formatter={(val, name) =>
                      name === "Ciro" ? fmtMoney(val) : val + " adet"}
                  />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="Sipariş"
                        stroke="#1565C0" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="Ciro"
                        stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Son 10 Sipariş */}
          <Card sx={{ borderRadius: 3 }}>
            <Box px={2.5} pt={2.5} pb={1} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>Son 10 Sipariş</Typography>
              <Button size="small" onClick={() => navigate("/orders")}>Tümü</Button>
            </Box>
            <Paper elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", fontSize: 12 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Müşteri</TableCell>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Tutar</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Alert severity="info">Henüz sipariş yok.</Alert>
                      </TableCell>
                    </TableRow>
                  ) : recent.map(o => (
                    <TableRow key={o.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>#{o.id}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.user_name || o.customer_name || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {o.created_at ? new Date(o.created_at).toLocaleString("tr-TR") : "—"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#1565C0" }}>
                        {fmtMoney(o.total_price ?? o.total)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[o.status] || o.status}
                          size="small"
                          sx={{
                            bgcolor: (STATUS_COLORS[o.status] || "#9CA3AF") + "22",
                            color: STATUS_COLORS[o.status] || "#9CA3AF",
                            fontWeight: 700, fontSize: 11
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => navigate("/orders")}>Detay</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Card>
        </Grid>

        {/* Sağ Kolon */}
        <Grid item xs={12} md={4}>
          {/* Top 10 Satılan Ürün */}
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <Box px={2.5} pt={2.5} pb={1} display="flex" alignItems="center" gap={1}>
              <StarIcon sx={{ color: "#F59E0B", fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700}>
                En Çok Satan (30 gün)
              </Typography>
            </Box>
            {topProducts.length === 0 ? (
              <Box px={2.5} pb={2.5}>
                <Alert severity="info">Son 30 günde satış yok.</Alert>
              </Box>
            ) : topProducts.map((p, i) => {
              const max = Number(topProducts[0]?.qty) || 1;
              const pct = (Number(p.qty) / max) * 100;
              return (
                <Box key={p.id} px={2.5} py={1} borderBottom="1px solid #f3f4f6">
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "#1565C0" }}>{i + 1}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{p.name}</Typography>
                      <Typography variant="caption" color="text.disabled">
                        {Number(p.qty).toLocaleString("tr-TR")} {p.unit || "adet"} · {fmtMoneyShort(p.revenue)}
                      </Typography>
                    </Box>
                  </Box>
                  <LinearProgress variant="determinate" value={pct}
                    sx={{ mt: 0.5, height: 4, borderRadius: 2,
                      "& .MuiLinearProgress-bar": { bgcolor: "#1565C0" } }} />
                </Box>
              );
            })}
          </Card>

          {/* Düşük Stok */}
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <Box px={2.5} pt={2.5} pb={1} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon sx={{ color: "#EF4444", fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700}>Düşük Stok Alarmı</Typography>
              </Box>
              <Button size="small" onClick={() => navigate("/stock")}>Stok Yönetimi</Button>
            </Box>
            {lowStock.length === 0 ? (
              <Box px={2.5} pb={2.5}>
                <Alert severity="success">Tüm stoklar yeterli</Alert>
              </Box>
            ) : lowStock.slice(0, 8).map(p => (
              <Box key={p.id} px={2.5} py={1.25}
                display="flex" alignItems="center" justifyContent="space-between"
                borderBottom="1px solid #f3f4f6">
                <Box sx={{ flex: 1, mr: 1, overflow: "hidden" }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{p.name}</Typography>
                  <Typography variant="caption" color="text.disabled">{p.category || "—"}</Typography>
                </Box>
                <Chip
                  label={`${p.stock} ${p.unit || ""}`}
                  size="small"
                  sx={{
                    bgcolor: Number(p.stock) === 0 ? "#EF444422" : "#F59E0B22",
                    color: Number(p.stock) === 0 ? "#EF4444" : "#F59E0B",
                    fontWeight: 700, fontSize: 11
                  }}
                />
              </Box>
            ))}
          </Card>

          {/* Aktif Kampanyalar */}
          <Card sx={{ borderRadius: 3 }}>
            <Box px={2.5} pt={2.5} pb={1} display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <CampaignIcon sx={{ color: "#8B5CF6", fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700}>Aktif Kampanyalar</Typography>
              </Box>
              <Button size="small" onClick={() => navigate("/campaigns")}>Yönet</Button>
            </Box>
            {campaigns.length === 0 ? (
              <Box px={2.5} pb={2.5}>
                <Alert severity="info">Aktif kampanya yok.</Alert>
              </Box>
            ) : campaigns.slice(0, 5).map(c => {
              const left = daysLeft(c.end_at);
              return (
                <Box key={c.id} px={2.5} py={1.25} borderBottom="1px solid #f3f4f6">
                  <Typography variant="body2" fontWeight={600} noWrap>{c.title}</Typography>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" color="text.disabled">
                      Başlangıç: {fmtDate(c.start_at)}
                    </Typography>
                    {left !== null ? (
                      <Chip
                        label={left === 0 ? "Bugün bitiyor" : `${left} gün kaldı`}
                        size="small"
                        sx={{
                          bgcolor: left <= 3 ? "#EF444422" : "#10B98122",
                          color: left <= 3 ? "#EF4444" : "#10B981",
                          fontSize: 11, fontWeight: 700
                        }}
                      />
                    ) : (
                      <Chip label="Süresiz" size="small"
                        sx={{ bgcolor: "#1565C022", color: "#1565C0", fontSize: 11, fontWeight: 700 }} />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
